// C:\IntegraNube_WebStore\src\components\Footer.js
'use strict';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link as RouterLink,
} from 'react-router-dom';

import api from '../api/api';

import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import {
  EmailOutlined as EmailIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LocalShippingOutlined as ShippingIcon,
  LocationOnOutlined as LocationIcon,
  PhoneOutlined as PhoneIcon,
  SecurityOutlined as SecurityIcon,
  ShoppingBagOutlined as ShoppingIcon,
  StorefrontOutlined as StorefrontIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

/* ===================================================================== */
/*  Helpers                                                              */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function cleanString(value) {
  const text =
    String(value || '').trim();

  return text || null;
}

function buildAddress(direccion = {}) {
  return [
    direccion?.direccion,
    direccion?.comuna,
    direccion?.ciudad,
    direccion?.region,
  ]
    .map((value) =>
      String(value || '').trim()
    )
    .filter(Boolean)
    .filter(
      (value, index, arr) =>
        arr.indexOf(value) ===
        index
    )
    .join(', ');
}

function normalizeWhatsApp(value) {
  const digits =
    String(value || '')
      .replace(/\D/g, '');

  if (
    digits.length === 9 &&
    digits.startsWith('9')
  ) {
    return `56${digits}`;
  }

  return digits || null;
}

function FooterLink({
  to,
  children,
}) {
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="none"
      sx={{
        color:
          'rgba(255,255,255,0.72)',
        fontSize: 14,
        lineHeight: 1.5,
        transition:
          'color 120ms ease, transform 120ms ease',

        '&:hover': {
          color: '#fff',
          transform:
            'translateX(2px)',
        },
      }}
    >
      {children}
    </Link>
  );
}


/*
 * Las páginas CMS se cargan desde Store API. Se excluyen las rutas que ya
 * aparecen de forma explícita en el footer para evitar enlaces duplicados.
 */
const FOOTER_STATIC_SLUGS = new Set([
  'productos',
  'marcas',
  'carrito',
  'contacto',
]);

function extractPublicPages(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.paginas)) return payload.paginas;
  if (Array.isArray(payload?.data?.paginas)) {
    return payload.data.paginas;
  }
  return [];
}

function normalizeFooterPages(items) {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const slug =
        cleanString(item?.slug)?.toLowerCase() ||
        null;

      const titulo =
        cleanString(item?.titulo) ||
        null;

      if (
        !slug ||
        !titulo ||
        slug.includes('/') ||
        slug.includes('.') ||
        FOOTER_STATIC_SLUGS.has(slug) ||
        seen.has(slug)
      ) {
        return null;
      }

      seen.add(slug);

      return {
        id:
          item?.id ??
          slug,
        slug,
        titulo,
        orden:
          Number.isFinite(Number(item?.orden))
            ? Number(item.orden)
            : 0,
      };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        a.orden - b.orden ||
        a.titulo.localeCompare(
          b.titulo,
          'es',
          { sensitivity: 'base' }
        )
    );
}

/* ===================================================================== */
/*  Footer                                                               */
/* ===================================================================== */

export default function Footer({
  tienda = null,
  config = null,
  storeConfig = null,
}) {
  const source =
    tienda ||
    config ||
    storeConfig ||
    {};

  const data =
    useMemo(
      () =>
        resolveTiendaData(source),
      [source]
    );

  const nombre =
    cleanString(data?.nombre) ||
    'Tienda Online';

  const nombreLegal =
    cleanString(
      data?.nombreLegal
    );

  const logoUrl =
    cleanString(
      data?.branding?.logo
    );

  const colorPrimario =
    cleanString(
      data?.branding?.colorPrimario
    ) ||
    '#1976d2';

  const email =
    cleanString(
      data?.contacto?.email
    );

  const telefono =
    cleanString(
      data?.contacto?.telefono
    );

  const whatsappRaw =
    cleanString(
      data?.contacto?.whatsapp
    );

  const whatsapp =
    normalizeWhatsApp(
      whatsappRaw
    );

  const direccion =
    buildAddress(
      data?.direccion
    );

  const instagram =
    cleanString(
      data?.redes?.instagram
    );

  const facebook =
    cleanString(
      data?.redes?.facebook
    );

  const tiktok =
    cleanString(
      data?.redes?.tiktok
    );

  const retiroDisponible =
    data?.ecommerce
      ?.retiroDisponible === true;

  const despachoDisponible =
    data?.ecommerce
      ?.despachoDisponible === true;

  const carritoHabilitado =
    data?.funciones
      ?.carrito !== false;

  const contactoHabilitado =
    data?.funciones
      ?.contacto !== false;

  const seguimientoHabilitado =
    data?.funciones
      ?.seguimientoPedidos !==
    false;

  const [
    logoError,
    setLogoError,
  ] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  const [
    paginasPublicas,
    setPaginasPublicas,
  ] = useState([]);

  useEffect(() => {
    const controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;

    let mounted = true;

    const loadPages = async () => {
      try {
        const response = await api.get(
          '/paginas',
          controller
            ? {
                signal: controller.signal,
                timeout: 6000,
                retries: 1,
              }
            : {
                timeout: 6000,
                retries: 1,
              }
        );

        if (!mounted) return;

        setPaginasPublicas(
          normalizeFooterPages(
            extractPublicPages(
              response?.data
            )
          )
        );
      } catch (error) {
        if (
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }

        /*
         * El footer nunca debe bloquear la tienda si el endpoint de páginas
         * falla temporalmente. Simplemente se omite la sección dinámica.
         */
        console.warn(
          '[Footer] No fue posible cargar páginas públicas:',
          error?.message || error
        );

        if (mounted) {
          setPaginasPublicas([]);
        }
      }
    };

    loadPages();

    return () => {
      mounted = false;
      controller?.abort();
    };
  }, []);

  const mostrarLogo =
    Boolean(logoUrl) &&
    !logoError;

  const beneficios = [
    {
      key: 'compra',
      icon: <ShoppingIcon />,
      titulo: 'Compra online',
      texto:
        'Explora productos y compra desde cualquier dispositivo.',
      visible: true,
    },

    {
      key: 'retiro',
      icon: <StorefrontIcon />,
      titulo: 'Retiro disponible',
      texto:
        'Compra online y retira según las condiciones de la tienda.',
      visible:
        retiroDisponible,
    },

    {
      key: 'despacho',
      icon: <ShippingIcon />,
      titulo: 'Despacho disponible',
      texto:
        'Consulta cobertura y opciones de entrega para tu compra.',
      visible:
        despachoDisponible,
    },

    {
      key: 'seguridad',
      icon: <SecurityIcon />,
      titulo: 'Compra informada',
      texto:
        'Revisa precio, disponibilidad y detalles antes de comprar.',
      visible:
        !retiroDisponible &&
        !despachoDisponible,
    },
  ].filter(
    (item) => item.visible
  );

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        color: '#fff',
        background:
          'linear-gradient(145deg, #0b1220 0%, #111827 56%, #172033 100%)',
        borderTop:
          `3px solid ${colorPrimario}`,
      }}
    >
      {/* =============================================================== */}
      {/* Beneficios                                                      */}
      {/* =============================================================== */}

      <Box
        sx={{
          borderBottom:
            '1px solid rgba(255,255,255,0.08)',
          bgcolor:
            'rgba(255,255,255,0.025)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: {
              xs: 2.5,
              md: 3,
            },
          }}
        >
          <Grid
            container
            spacing={2}
            justifyContent="center"
          >
            {beneficios.map(
              (item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={
                    beneficios.length >=
                    3
                      ? 4
                      : 6
                  }
                  key={item.key}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: 2,
                        display:
                          'grid',
                        placeItems:
                          'center',
                        color:
                          colorPrimario,
                        bgcolor:
                          `${colorPrimario}1c`,
                        border:
                          `1px solid ${colorPrimario}35`,

                        '& svg': {
                          fontSize: 23,
                        },
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight:
                            850,
                          fontSize: 14,
                        }}
                      >
                        {item.titulo}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.2,
                          display:
                            'block',
                          color:
                            'rgba(255,255,255,0.58)',
                          lineHeight:
                            1.45,
                        }}
                      >
                        {item.texto}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )
            )}
          </Grid>
        </Container>
      </Box>

      {/* =============================================================== */}
      {/* Contenido principal                                             */}
      {/* =============================================================== */}

      <Container
        maxWidth="xl"
        data-nosnippet=""
        sx={{
          pt: {
            xs: 4,
            md: 5.5,
          },
          pb: {
            xs: 3,
            md: 4,
          },
        }}
      >
        <Grid
          container
          spacing={{
            xs: 4,
            md: 5,
          }}
        >
          {/* =========================================================== */}
          {/* Marca                                                       */}
          {/* =========================================================== */}

          <Grid
            item
            xs={12}
            md={
              paginasPublicas.length
                ? 4
                : 5
            }
            lg={
              paginasPublicas.length
                ? 3.5
                : 4.5
            }
          >
            <Box
              sx={{
                maxWidth: 420,
              }}
            >
              <Box
                component={RouterLink}
                to="/"
                aria-label={`Ir al inicio de ${nombre}`}
                sx={{
                  display:
                    'inline-flex',
                  alignItems:
                    'center',
                  textDecoration:
                    'none',
                  color: 'inherit',
                  maxWidth:
                    '100%',
                }}
              >
                {mostrarLogo ? (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt={`Logo ${nombre}`}
                    loading="lazy"
                    decoding="async"
                    onError={() =>
                      setLogoError(true)
                    }
                    sx={{
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: {
                        xs: 190,
                        sm: 230,
                      },
                      maxHeight: 72,
                      objectFit:
                        'contain',
                      objectPosition:
                        'left center',
                      bgcolor: '#fff',
                      borderRadius: 2,
                      p: 0.75,
                    }}
                  />
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <StorefrontIcon
                      sx={{
                        color:
                          colorPrimario,
                        fontSize: 30,
                      }}
                    />

                    <Typography
                      sx={{
                        fontWeight:
                          900,
                        fontSize: 22,
                      }}
                    >
                      {nombre}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 390,
                  color:
                    'rgba(255,255,255,0.66)',
                  fontSize: 14,
                  lineHeight: 1.75,
                }}
              >
                Compra online en{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                  }}
                >
                  {nombre}
                </Box>
                . Revisa nuestro catálogo,
                disponibilidad y productos
                destacados.
              </Typography>

              {nombreLegal &&
              nombreLegal !== nombre ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    color:
                      'rgba(255,255,255,0.4)',
                  }}
                >
                  {nombreLegal}
                </Typography>
              ) : null}

              {(instagram ||
                facebook ||
                whatsapp ||
                tiktok) ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ mt: 2.25 }}
                >
                  {instagram ? (
                    <IconButton
                      component="a"
                      href={instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      sx={{
                        color: '#fff',
                        bgcolor:
                          'rgba(255,255,255,0.08)',

                        '&:hover': {
                          bgcolor:
                            `${colorPrimario}40`,
                          color:
                            colorPrimario,
                        },
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                  ) : null}

                  {facebook ? (
                    <IconButton
                      component="a"
                      href={facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      sx={{
                        color: '#fff',
                        bgcolor:
                          'rgba(255,255,255,0.08)',

                        '&:hover': {
                          bgcolor:
                            `${colorPrimario}40`,
                          color:
                            colorPrimario,
                        },
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                  ) : null}

                  {whatsapp ? (
                    <IconButton
                      component="a"
                      href={`https://wa.me/${whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="WhatsApp"
                      sx={{
                        color: '#fff',
                        bgcolor:
                          'rgba(255,255,255,0.08)',

                        '&:hover': {
                          bgcolor:
                            'rgba(37,211,102,0.20)',
                          color:
                            '#25D366',
                        },
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Grid>

          {/* =========================================================== */}
          {/* Navegación                                                  */}
          {/* =========================================================== */}

          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            lg={2}
          >
            <Typography
              sx={{
                mb: 1.6,
                fontSize: 13,
                fontWeight: 900,
                textTransform:
                  'uppercase',
                letterSpacing:
                  '0.065em',
                color: '#fff',
              }}
            >
              Tienda
            </Typography>

            <Stack spacing={1.05}>
              <FooterLink to="/">
                Inicio
              </FooterLink>

              <FooterLink to="/productos">
                Productos
              </FooterLink>

              <FooterLink to="/marcas">
                Marcas
              </FooterLink>

              {carritoHabilitado ? (
                <FooterLink to="/carrito">
                  Carrito
                </FooterLink>
              ) : null}

              {contactoHabilitado ? (
                <FooterLink to="/contacto">
                  Contacto
                </FooterLink>
              ) : null}

              {seguimientoHabilitado ? (
                <FooterLink to="/seguimiento-pedido">
                  Seguimiento de pedidos
                </FooterLink>
              ) : null}
            </Stack>
          </Grid>

          {paginasPublicas.length ? (
            <Grid
              item
              xs={6}
              sm={4}
              md={2}
              lg={2}
            >
              <Typography
                sx={{
                  mb: 1.6,
                  fontSize: 13,
                  fontWeight: 900,
                  textTransform:
                    'uppercase',
                  letterSpacing:
                    '0.065em',
                  color: '#fff',
                }}
              >
                Información
              </Typography>

              <Stack spacing={1.05}>
                {paginasPublicas.map(
                  (pagina) => (
                    <FooterLink
                      key={
                        pagina.id ||
                        pagina.slug
                      }
                      to={`/${encodeURIComponent(
                        pagina.slug
                      )}`}
                    >
                      {pagina.titulo}
                    </FooterLink>
                  )
                )}
              </Stack>
            </Grid>
          ) : null}

          {/* =========================================================== */}
          {/* Contacto                                                    */}
          {/* =========================================================== */}

          <Grid
            item
            xs={12}
            sm={8}
            md={
              paginasPublicas.length
                ? 4
                : 5
            }
            lg={
              paginasPublicas.length
                ? 4.5
                : 5.5
            }
          >
            <Typography
              sx={{
                mb: 1.6,
                fontSize: 13,
                fontWeight: 900,
                textTransform:
                  'uppercase',
                letterSpacing:
                  '0.065em',
                color: '#fff',
              }}
            >
              Contacto
            </Typography>

            <Stack
              spacing={1.35}
              sx={{
                maxWidth: 440,
              }}
            >
              {direccion ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                >
                  <LocationIcon
                    sx={{
                      mt: 0.15,
                      color:
                        colorPrimario,
                      fontSize: 20,
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        'rgba(255,255,255,0.68)',
                      lineHeight: 1.6,
                    }}
                  >
                    {direccion}
                  </Typography>
                </Stack>
              ) : null}

              {email ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <EmailIcon
                    sx={{
                      color:
                        colorPrimario,
                      fontSize: 20,
                    }}
                  />

                  <Link
                    href={`mailto:${email}`}
                    underline="hover"
                    sx={{
                      color:
                        'rgba(255,255,255,0.72)',
                      fontSize: 14,
                    }}
                  >
                    {email}
                  </Link>
                </Stack>
              ) : null}

              {telefono ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <PhoneIcon
                    sx={{
                      color:
                        colorPrimario,
                      fontSize: 20,
                    }}
                  />

                  <Link
                    href={`tel:${telefono}`}
                    underline="hover"
                    sx={{
                      color:
                        'rgba(255,255,255,0.72)',
                      fontSize: 14,
                    }}
                  >
                    {telefono}
                  </Link>
                </Stack>
              ) : null}

              {whatsappRaw ? (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <WhatsAppIcon
                    sx={{
                      color:
                        '#25D366',
                      fontSize: 20,
                    }}
                  />

                  <Link
                    href={
                      whatsapp
                        ? `https://wa.me/${whatsapp}`
                        : undefined
                    }
                    target={
                      whatsapp
                        ? '_blank'
                        : undefined
                    }
                    rel={
                      whatsapp
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    underline="hover"
                    sx={{
                      color:
                        'rgba(255,255,255,0.72)',
                      fontSize: 14,
                    }}
                  >
                    {whatsappRaw}
                  </Link>
                </Stack>
              ) : null}
            </Stack>
          </Grid>
        </Grid>

        {/* ============================================================= */}
        {/* Pie inferior                                                  */}
        {/* ============================================================= */}

        <Divider
          sx={{
            mt: {
              xs: 4,
              md: 5,
            },
            mb: 2.5,
            borderColor:
              'rgba(255,255,255,0.09)',
          }}
        />

        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1}
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
          justifyContent="space-between"
        >
          <Typography
            variant="caption"
            sx={{
              color:
                'rgba(255,255,255,0.45)',
            }}
          >
            ©{' '}
            {new Date().getFullYear()}{' '}
            {nombre}. Todos los derechos
            reservados.
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
          >
            <SecurityIcon
              sx={{
                fontSize: 16,
                color:
                  colorPrimario,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color:
                  'rgba(255,255,255,0.45)',
              }}
            >
              Tienda online
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
